
import { useLocation } from "react-router-dom";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import Layout from "@/components/Layout/Layout";
import { Card, CardContent } from "@/components/ui/card";
import { Home, Phone, ArrowRight, AlertTriangle } from "lucide-react";

const NotFound = () => {
  const location = useLocation();

  useEffect(() => {
    console.error(
      "404 Error: User attempted to access non-existent route:",
      location.pathname
    );
    
    // Set proper HTTP status for SEO
    if (typeof window !== 'undefined' && window.history) {
      // This ensures search engines get the proper 404 status
      document.title = "404 - Page Not Found | Carolina Trophy Roofs";
    }
  }, [location.pathname]);

  return (
    <Layout>
      <Helmet>
        <title>404 - Page Not Found | Carolina Trophy Roofs</title>
        <meta name="description" content="Sorry, the page you're looking for doesn't exist. Find roofing services, locations, and resources at Carolina Trophy Roofs." />
        <meta name="robots" content="noindex, nofollow" />
        <link rel="canonical" href="https://www.carolinatrophyroofs.com/404" />
      </Helmet>
      
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-gray-50 to-white py-16">
        <div className="container mx-auto px-4">
          <Card className="max-w-2xl mx-auto text-center">
            <CardContent className="p-12">
              <div className="w-20 h-20 bg-brand-safety-orange/10 rounded-full flex items-center justify-center mx-auto mb-8">
                <AlertTriangle className="h-10 w-10 text-brand-safety-orange" />
              </div>
              
              <h1 className="text-6xl font-bold text-brand-charcoal-gray mb-4">404</h1>
              <h2 className="text-3xl font-bold text-brand-charcoal-gray mb-4">Page Not Found</h2>
              
              <p className="text-xl text-muted-foreground mb-6">
                We can't find the page you're looking for.
              </p>
              
              <p className="text-muted-foreground mb-8">
                The page at <code className="bg-gray-100 px-2 py-1 rounded">{location.pathname}</code> might have been moved, 
                deleted, or the URL was typed incorrectly.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 justify-center mb-8">
                <Button asChild className="bg-brand-safety-orange hover:bg-brand-safety-orange/90 text-white">
                  <Link to="/">
                    <Home className="mr-2 h-4 w-4" />
                    Go to Home Page
                  </Link>
                </Button>
                
                <Button asChild variant="outline" className="border-brand-charcoal-gray text-brand-charcoal-gray hover:bg-brand-charcoal-gray hover:text-white">
                  <Link to="/contact">
                    <Phone className="mr-2 h-4 w-4" />
                    Contact Us
                  </Link>
                </Button>
              </div>

              <div className="text-sm text-muted-foreground">
                <p className="mb-4 font-semibold text-brand-charcoal-gray">Popular Pages:</p>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 text-brand-safety-orange">
                  <Link to="/services" className="hover:underline flex items-center justify-center">
                    Our Services
                  </Link>
                  <Link to="/locations" className="hover:underline flex items-center justify-center">
                    Service Areas
                  </Link>
                  <Link to="/blog" className="hover:underline flex items-center justify-center">
                    Blog
                  </Link>
                  <Link to="/frequently-asked-questions" className="hover:underline flex items-center justify-center">
                    FAQ
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </Layout>
  );
};

export default NotFound;
